import {
  ImageIcon,
  Minus,
  Package,
  Plus,
  ShoppingBag,
  Trash,
} from "@/components/icons";
import { Badge, Button } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { OrderLineLotPick } from "@/modules/orders/presentation/lib/order-lot-allocation";
import { formatLak } from "@/shared/lib/currency";
import { AppImage } from "@/shared/ui/AppImage";

export type OrderLineKitComponent = {
  componentVariantId: string;
  quantity: number;
  productName: string;
  variantName: string;
  stockQuantity: number;
};

export type OrderLineItem = {
  lineKey: string;
  lineType?: "simple" | "ad_hoc_kit";
  kitName?: string | null;
  kitComponents?: OrderLineKitComponent[];
  variantId: string;
  productName: string;
  variantName: string;
  saleUnitPrice: string;
  imageKey: string | null;
  stockQuantity: number;
  stockUnitLabel: string;
  unitsPerSale: number;
  quantity: number;
  isBonus: boolean;
  lotPicks: OrderLineLotPick[];
};

type OrderLineItemsProps = {
  items: OrderLineItem[];
  bumpedLineKey?: string | null;
  onChangeQty: (lineKey: string, delta: number) => void;
  onRemove: (lineKey: string) => void;
  onToggleBonus?: (lineKey: string) => void;
  onAddBonusForLine?: (lineKey: string) => void;
  onAddClick?: () => void;
  onAddBonusClick?: () => void;
  onAddKitClick?: () => void;
  onEditLots?: (lineKey: string) => void;
};

type LineAction = {
  key: string;
  label: string;
  onClick: () => void;
};

function LineActionChip({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 shrink-0 rounded-full px-3 text-xs"
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

function OrderLineItemCard({
  item,
  index,
  isBumped,
  onChangeQty,
  onRemove,
  onToggleBonus,
  onAddBonusForLine,
  onEditLots,
}: {
  item: OrderLineItem;
  index: number;
  isBumped: boolean;
  onChangeQty: (lineKey: string, delta: number) => void;
  onRemove: (lineKey: string) => void;
  onToggleBonus?: (lineKey: string) => void;
  onAddBonusForLine?: (lineKey: string) => void;
  onEditLots?: (lineKey: string) => void;
}) {
  const unitPrice = item.isBonus ? 0 : Number(item.saleUnitPrice);
  const lineTotal = unitPrice * item.quantity;

  const secondaryActions: LineAction[] = [];
  const isKit = item.lineType === "ad_hoc_kit";
  if (onEditLots && !isKit) {
    secondaryActions.push({
      key: "lot",
      label: "Lot",
      onClick: () => onEditLots(item.lineKey),
    });
  }
  if (!item.isBonus && onAddBonusForLine && !isKit) {
    secondaryActions.push({
      key: "bonus",
      label: "ແຖມ +1",
      onClick: () => onAddBonusForLine(item.lineKey),
    });
  }
  if (onToggleBonus && !isKit && (item.isBonus || item.quantity === 1)) {
    secondaryActions.push({
      key: "toggle-bonus",
      label: item.isBonus ? "ຕັ້ງເປັນຂາຍ" : "ຕັ້ງເປັນແຖມ",
      onClick: () => onToggleBonus(item.lineKey),
    });
  }

  return (
    <li
      className="animate-order-item-enter"
      style={{ animationDelay: `${Math.min(index, 4) * 40}ms` }}
    >
      <article
        className={cn(
          "overflow-hidden rounded-xl border border-secondary/20 bg-background/90",
          isBumped && "ring-2 ring-secondary/40",
          item.isBonus && "border-secondary/35 bg-secondary/5",
          isKit && "border-amber-600/25",
        )}
      >
        <div className="flex gap-3 p-3 pb-2">
          <div className="size-14 shrink-0 overflow-hidden rounded-xl bg-muted/40 sm:size-16">
            {item.imageKey ? (
              <AppImage
                src={item.imageKey}
                alt={item.productName}
                className="size-full"
                fit="cover"
              />
            ) : (
              <div className="flex size-full items-center justify-center text-muted-foreground">
                {isKit ? (
                  <Package className="opacity-50" />
                ) : (
                  <ImageIcon className="opacity-40" />
                )}
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="line-clamp-2 font-medium text-foreground text-sm leading-snug">
                    {item.productName}
                  </p>
                  {isKit ? (
                    <Badge
                      variant="outline"
                      className="rounded-full border-amber-600/30 bg-amber-500/10 text-[10px] text-amber-900"
                    >
                      ຊຸດກຳນົດເອງ
                    </Badge>
                  ) : null}
                  {item.isBonus ? (
                    <Badge
                      variant="secondary"
                      className="rounded-full text-[10px]"
                    >
                      ແຖມ
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-0.5 text-muted-foreground text-xs">
                  {item.variantName}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-display text-base text-foreground tabular-nums leading-none">
                  {item.isBonus ? "ຟຣີ" : formatLak(lineTotal)}
                </p>
                {!item.isBonus ? (
                  <p className="mt-0.5 text-[10px] text-muted-foreground tabular-nums">
                    {formatLak(item.saleUnitPrice)} / ລາຍການ
                  </p>
                ) : null}
              </div>
            </div>

            {isKit && item.kitComponents && item.kitComponents.length > 0 ? (
              <ul className="mt-1.5 flex flex-col gap-0.5 text-[11px] text-muted-foreground">
                {item.kitComponents.map((comp) => (
                  <li
                    key={comp.componentVariantId}
                    className="flex justify-between gap-2"
                  >
                    <span className="min-w-0 truncate">
                      {comp.productName} · {comp.variantName}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      ×{comp.quantity * item.quantity}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}

            {!isKit && item.lotPicks.length > 0 ? (
              <p className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground">
                <Package className="size-3 shrink-0" />
                {item.lotPicks.map((pick) => (
                  <span key={pick.stockLotId} className="tabular-nums">
                    {pick.receiptNumber} × {pick.quantity}
                  </span>
                ))}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-2 border-secondary/15 border-t px-3 py-2.5">
          <div className="inline-flex items-center rounded-full border border-secondary/25 bg-muted/20 p-0.5">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-9 rounded-full"
              onClick={() => onChangeQty(item.lineKey, -1)}
              aria-label="ຫຼຸດຈຳນວນ"
            >
              <Minus />
            </Button>
            <span
              className={cn(
                "flex min-w-9 items-center justify-center font-semibold text-base tabular-nums",
                isBumped && "animate-qty-bump text-secondary",
              )}
            >
              {item.quantity}
            </span>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-9 rounded-full"
              onClick={() => onChangeQty(item.lineKey, 1)}
              aria-label="ເພີ່ມຈຳນວນ"
            >
              <Plus />
            </Button>
          </div>

          <div className="ml-auto flex min-w-0 items-center justify-end gap-1">
            <div className="hidden min-w-0 items-center gap-1.5 md:flex">
              {secondaryActions.map((action) => (
                <LineActionChip
                  key={action.key}
                  label={action.label}
                  onClick={action.onClick}
                />
              ))}
            </div>

            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-9 shrink-0 rounded-full text-destructive hover:bg-destructive/10"
              onClick={() => onRemove(item.lineKey)}
              aria-label="ລຶບລາຍການ"
            >
              <Trash />
            </Button>
          </div>
        </div>

        {secondaryActions.length > 0 ? (
          <div className="scrollbar-none flex gap-1.5 overflow-x-auto px-3 pb-3 [-ms-overflow-style:none] md:hidden [&::-webkit-scrollbar]:hidden">
            {secondaryActions.map((action) => (
              <LineActionChip
                key={`${action.key}-mobile`}
                label={action.label}
                onClick={action.onClick}
              />
            ))}
          </div>
        ) : null}
      </article>
    </li>
  );
}

export function OrderLineItems({
  items,
  bumpedLineKey,
  onChangeQty,
  onRemove,
  onToggleBonus,
  onAddBonusForLine,
  onAddClick,
  onAddBonusClick,
  onAddKitClick,
  onEditLots,
}: OrderLineItemsProps) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-xl border border-secondary/20 border-dashed px-4 py-10 text-center">
        <ShoppingBag className="size-10 text-muted-foreground/50" />
        <div className="flex flex-col gap-1">
          <p className="font-medium text-foreground text-sm">ຍັງບໍ່ມີສິນຄ້າ</p>
          <p className="text-muted-foreground text-xs">
            ເພີ່ມສິນຄ້າຂາຍ ຫຼື ຈັດເປັນເຊັດເພື່ອເລີ່ມສ້າງອໍເດີ້
          </p>
        </div>
        <div className="grid w-full max-w-sm grid-cols-2 gap-2 sm:grid-cols-3">
          {onAddClick ? (
            <Button
              type="button"
              className="h-11 rounded-full"
              onClick={onAddClick}
            >
              <Plus data-icon="inline-start" />
              ສິນຄ້າ
            </Button>
          ) : null}
          {onAddKitClick ? (
            <Button
              type="button"
              variant="outline"
              className="h-11 rounded-full"
              onClick={onAddKitClick}
            >
              <Package data-icon="inline-start" />
              ຈັດເຊັດ
            </Button>
          ) : null}
          {onAddBonusClick ? (
            <Button
              type="button"
              variant="outline"
              className="h-11 rounded-full"
              onClick={onAddBonusClick}
            >
              <Plus data-icon="inline-start" />
              ແຖມ
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {items.map((item, index) => (
        <OrderLineItemCard
          key={item.lineKey}
          item={item}
          index={index}
          isBumped={bumpedLineKey === item.lineKey}
          onChangeQty={onChangeQty}
          onRemove={onRemove}
          onToggleBonus={onToggleBonus}
          onAddBonusForLine={onAddBonusForLine}
          onEditLots={onEditLots}
        />
      ))}
    </ul>
  );
}

export function OrderLineItemsAddBar({
  onAddClick,
  onAddBonusClick,
  onAddKitClick,
  className,
}: {
  onAddClick?: () => void;
  onAddBonusClick?: () => void;
  onAddKitClick?: () => void;
  className?: string;
}) {
  if (!onAddClick && !onAddBonusClick && !onAddKitClick) return null;

  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end",
        className,
      )}
    >
      {onAddClick ? (
        <Button
          type="button"
          className="h-10 rounded-full sm:h-9"
          onClick={onAddClick}
        >
          <Plus data-icon="inline-start" />
          ເພີ່ມສິນຄ້າ
        </Button>
      ) : null}
      {onAddKitClick ? (
        <Button
          type="button"
          variant="outline"
          className="h-10 rounded-full sm:h-9"
          onClick={onAddKitClick}
        >
          <Package data-icon="inline-start" />
          ຈັດເປັນເຊັດ
        </Button>
      ) : null}
      {onAddBonusClick ? (
        <Button
          type="button"
          variant="outline"
          className="h-10 rounded-full sm:h-9"
          onClick={onAddBonusClick}
        >
          <Plus data-icon="inline-start" />
          ເພີ່ມແຖມ
        </Button>
      ) : null}
    </div>
  );
}
